import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { remediationsApi } from "../api/endpoints";
import {
  REMEDIATION_STATUS_LABELS,
  RemediationChip,
} from "../components/remediation/RemediationChip";

/**
 * RemediationInboxPage — workspace-scoped table of remediations.
 *
 * Read-only page — creation happens via the FindingCard chip. Filters
 * are querystring-driven so links are shareable.
 */
export default function RemediationInboxPage() {
  const { workspaceId } = useParams();
  const [filters, setFilters] = useState({
    status: "",
    target_from: "",
    target_to: "",
  });
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setItems(null);
    remediationsApi
      .list(workspaceId, {
        status: filters.status,
        target_from: filters.target_from,
        target_to: filters.target_to,
        size: 100,
      })
      .then((res) => {
        if (!alive) return;
        setItems(res.items || []);
        setTotal(res.total || 0);
      })
      .catch((e) => alive && setError(e));
    return () => {
      alive = false;
    };
  }, [workspaceId, filters]);

  const empty = items !== null && items.length === 0;
  const statusOptions = useMemo(
    () => Object.entries(REMEDIATION_STATUS_LABELS).filter(([k]) => k !== "open"),
    [],
  );

  return (
    <section data-testid="remediation-inbox-page" className="space-y-4">
      <header>
        <h1 className="text-lg font-semibold text-slate-900">Remediation</h1>
        <p className="mt-1 max-w-2xl text-sm text-slate-600">
          Workspace-wide remediation state layered on top of frozen deterministic findings.
          The finding documents themselves are never modified — only the status,
          notes, and target date live here.
        </p>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 rounded border border-slate-200 bg-slate-50 px-3 py-2">
        <label className="text-xs">
          <span className="block text-[10px] uppercase tracking-widest text-slate-500">
            Status
          </span>
          <select
            value={filters.status}
            onChange={(e) =>
              setFilters((p) => ({ ...p, status: e.target.value }))
            }
            data-testid="remediation-filter-status"
            className="mt-1 rounded border border-slate-300 bg-white px-2 py-1 text-xs"
          >
            <option value="">All</option>
            {statusOptions.map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </select>
        </label>
        <label className="text-xs">
          <span className="block text-[10px] uppercase tracking-widest text-slate-500">
            Target from
          </span>
          <input
            type="date"
            value={filters.target_from}
            onChange={(e) =>
              setFilters((p) => ({ ...p, target_from: e.target.value }))
            }
            data-testid="remediation-filter-target-from"
            className="mt-1 rounded border border-slate-300 bg-white px-2 py-1 text-xs"
          />
        </label>
        <label className="text-xs">
          <span className="block text-[10px] uppercase tracking-widest text-slate-500">
            Target to
          </span>
          <input
            type="date"
            value={filters.target_to}
            onChange={(e) =>
              setFilters((p) => ({ ...p, target_to: e.target.value }))
            }
            data-testid="remediation-filter-target-to"
            className="mt-1 rounded border border-slate-300 bg-white px-2 py-1 text-xs"
          />
        </label>
        <div className="ml-auto text-xs text-slate-600">
          Total: <span data-testid="remediation-total">{total}</span>
        </div>
      </div>

      {error && (
        <div
          data-testid="remediation-inbox-error"
          className="rounded border border-red-200 bg-red-50 p-3 text-xs text-red-800"
        >
          Could not load remediations: {error?.body?.error?.message || "unknown error"}.
        </div>
      )}

      {items === null && !error && (
        <div data-testid="remediation-inbox-loading" className="text-sm text-slate-500">
          Loading…
        </div>
      )}

      {empty && (
        <div
          data-testid="remediation-inbox-empty"
          className="rounded border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-slate-600"
        >
          Findings that need remediation attention will appear here. Open a
          finding and tap the status chip to start.
        </div>
      )}

      {items && items.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500">
            <tr className="border-b border-slate-200">
              <th className="px-2 py-2 text-left font-medium">Status</th>
              <th className="px-2 py-2 text-left font-medium">Finding</th>
              <th className="px-2 py-2 text-left font-medium">Process</th>
              <th className="px-2 py-2 text-left font-medium">Owner</th>
              <th className="px-2 py-2 text-left font-medium">Target date</th>
              <th className="px-2 py-2 text-left font-medium">Updated</th>
            </tr>
          </thead>
          <tbody className="text-xs">
            {items.map((r) => (
              <tr
                key={r.id}
                data-testid={`remediation-row-${r.id}`}
                className="border-b border-slate-100 hover:bg-slate-50"
              >
                <td className="px-2 py-2">
                  <RemediationChip
                    status={r.status}
                    onClick={() => undefined}
                    testId={`remediation-row-chip-${r.id}`}
                  />
                </td>
                <td className="px-2 py-2">
                  {r.run_id ? (
                    <Link
                      to={`/w/${workspaceId}/analyses/${r.run_id}/processes/${r.process_id}`}
                      className="text-slate-700 underline hover:text-slate-900"
                    >
                      <code>{r.finding_id.slice(0, 8)}</code>
                    </Link>
                  ) : (
                    <code>{r.finding_id.slice(0, 8)}</code>
                  )}
                </td>
                <td className="px-2 py-2 text-slate-800">{r.process_id || "—"}</td>
                <td className="px-2 py-2 text-slate-800">
                  <code>{(r.owner_user_id || "").slice(0, 8) || "—"}</code>
                </td>
                <td className="px-2 py-2 text-slate-800">
                  {r.target_date || "—"}
                </td>
                <td className="px-2 py-2 text-slate-500">
                  {r.updated_at ? (
                    <time dateTime={r.updated_at}>
                      {r.updated_at.split("T")[0]}
                    </time>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
